import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { TrendingUp, Info } from "lucide-react";

export default function Forecast() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales/forecast?days=${d}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <div data-testid="forecast-page">
      <PageHeader eyebrow="Previsione" title="Forecast vendite"
        subtitle="Basata sullo storico scontrini per giorno-della-settimana e sulla stagionalità turistica di Orosei."
        actions={
          <div className="flex gap-2">
            <Button data-testid="fc-7" variant={days === 7 ? "default" : "outline"} onClick={() => setDays(7)} className={days === 7 ? "bg-neutral-900 text-[#FDFBF7]" : ""}>7 giorni</Button>
            <Button data-testid="fc-30" variant={days === 30 ? "default" : "outline"} onClick={() => setDays(30)} className={days === 30 ? "bg-neutral-900 text-[#FDFBF7]" : ""}>30 giorni</Button>
            <Button data-testid="fc-90" variant={days === 90 ? "default" : "outline"} onClick={() => setDays(90)} className={days === 90 ? "bg-neutral-900 text-[#FDFBF7]" : ""}>90 giorni</Button>
          </div>
        } />

      {loading || !data ? (
        <div className="text-neutral-500">Calcolo previsione…</div>
      ) : (
        <>
          <div className={`border rounded-lg p-4 mb-6 flex items-start gap-3 text-sm ${data.confidence === "bassa" ? "bg-amber-50 border-amber-300" : "bg-[#F3EDE1] border-[#E2D9C8]"}`}>
            <Info size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <div>
              <div><strong>Affidabilità: {data.confidence}</strong> · {data.history_days} giorni di storico</div>
              <div className="text-neutral-600 mt-1">{data.note}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard label={`Fatturato previsto ${days}g`} value={fmtEUR(data.total_predicted_revenue)} testid="fc-total" />
            <StatCard label="Media giornaliera" value={fmtEUR(data.avg_daily)} testid="fc-avg" />
            <StatCard label="Scontrini previsti" value={fmtInt(data.total_predicted_tickets)} testid="fc-tickets" />
          </div>

          <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white mb-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} strokeWidth={1.5} />
              <div>
                <div className="label-eyebrow">Curva previsionale</div>
                <h3 className="font-serif text-2xl">Prossimi {days} giorni</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.forecast}>
                <CartesianGrid stroke="#E2D9C8" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5C5C5C" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#5C5C5C" }} />
                <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #E2D9C8", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                <ReferenceLine y={data.avg_daily} stroke="#D7C4A3" strokeDasharray="4 4" label={{ value: "media", position: "right", fill: "#5C5C5C", fontSize: 10 }} />
                <Line type="monotone" dataKey="predicted_revenue" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm zebra min-w-[640px]">
              <thead><tr className="text-left label-eyebrow">
                <th className="p-3">Data</th><th className="p-3">Giorno</th>
                <th className="p-3 text-right">Fatturato previsto</th>
                <th className="p-3 text-right">Scontrini</th>
                <th className="p-3 text-right">Fattore stagionale</th>
              </tr></thead>
              <tbody>
                {data.forecast.map((f) => (
                  <tr key={f.date} className="border-t border-[#E2D9C8]" data-testid={`fc-row-${f.date}`}>
                    <td className="p-3">{f.date}</td>
                    <td className="p-3">{f.weekday}</td>
                    <td className="p-3 text-right font-medium">{fmtEUR(f.predicted_revenue)}</td>
                    <td className="p-3 text-right">{f.predicted_tickets}</td>
                    <td className="p-3 text-right"><span className={`text-xs px-2 py-0.5 rounded-full ${f.seasonality_mult > 1.1 ? "bg-emerald-100 text-emerald-800" : f.seasonality_mult < 0.9 ? "bg-neutral-100 text-neutral-600" : "bg-[#F3EDE1] text-neutral-700"}`}>×{f.seasonality_mult}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
